import { useMemo } from 'react';

import { useSelector } from 'editor/src/store/hooks';
import {
  SingleOptions,
  ProductVariation,
  VariationProductControl,
  ProductControlOption,
  ProductControlOptionDefault,
} from 'editor/src/store/variants/types';

import { RootState } from 'editor/src/store';

// Cache for existing options calculations
const existingOptionsCache = new Map<string, ProductControlOption[]>();

export function getExistingOptions<P extends ProductControlOption>(
  controlKey: string,
  options: P[],
  singleOptions: SingleOptions,
  variations: ProductVariation[],
): P[] {
  // Create enhanced cache key that includes product fingerprint to auto-invalidate
  const singleOptionsStr = Object.entries(singleOptions)
    .sort()
    .map(([key, value]) => `${key}:${value}`)
    .join('|');
  const optionsStr = options
    .map((o) => o.value)
    .sort()
    .join(',');
  const productFingerprint = variations[0]?.productUid || 'none';
  const cacheKey = `${controlKey}:${singleOptionsStr}:${optionsStr}:${variations.length}:${productFingerprint}`;

  // Return cached result if available (and refresh its position in LRU)
  const cached = existingOptionsCache.get(cacheKey) as P[];
  if (cached) {
    // Refresh LRU position by deleting and re-inserting
    existingOptionsCache.delete(cacheKey);
    existingOptionsCache.set(cacheKey, cached);
    return cached;
  }

  const singleOptionKeys = Object.keys(singleOptions);

  // Pre-filter variations that match single options for efficiency
  const matchingVariations =
    singleOptionKeys.length > 0
      ? variations.filter((v) => singleOptionKeys.every((key) => v[key] === singleOptions[key]))
      : variations;

  // Create set of existing values for O(1) lookup
  const existingValues = new Set(matchingVariations.map((v) => v[controlKey]));

  const result = options.filter((option) => existingValues.has(option.value));

  // Cache the result
  existingOptionsCache.set(cacheKey, result);

  // Limit cache size to prevent memory leaks
  if (existingOptionsCache.size > 50) {
    const firstKey = existingOptionsCache.keys().next().value;
    if (firstKey) {
      existingOptionsCache.delete(firstKey);
    }
  }

  return result;
}

function useFilteredOptionsWithVariations(
  control: VariationProductControl,
  variations: ProductVariation[],
  singleOptions: SingleOptions,
  disabledControlDescription: string | undefined,
) {
  // GPS-25623 — lazy-variant flows publish the full catalogue palette via
  // `productControls.options` and resolve picks on demand through
  // `variants.requestVariantByAttributes`. In this mode `productVariations` no
  // longer authoritatively enumerates the picker universe — it tracks only
  // the variants the host has currently materialised — so filtering options
  // through it would collapse the palette as soon as the picked set is
  // smaller than the catalogue. Honour the contract by skipping the filter
  // entirely when the host opted in via `configuration.useLazyVariantLoading`.
  const useLazyVariantLoading = useSelector(
    (state: RootState) => !!state.variants.configuration?.useLazyVariantLoading,
  );

  return useMemo(() => {
    const existingOptions = useLazyVariantLoading
      ? control.options
      : getExistingOptions(control.key, control.options, singleOptions, variations);
    if (disabledControlDescription) {
      return existingOptions.map<ProductControlOptionDefault>((option) => ({
        ...option,
        disabledTitle: disabledControlDescription,
      }));
    }

    return existingOptions;
  }, [singleOptions, control, disabledControlDescription, variations, useLazyVariantLoading]);
}

// Utility function to clear the existing options cache
export function clearExistingOptionsCache() {
  existingOptionsCache.clear();
}

export default useFilteredOptionsWithVariations;
